import { Injectable } from '@angular/core';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  AprimaCacheService,
  DoseRangeUtil,
  NumUtil,
  ServerInfoRepository,
} from 'webcommon/legacy-common';
import {
  DoseRangeInfo,
  DosingCalculatorModalParams,
  Guid,
  GuidStrValue,
  PatientDosingDetailsDto,
  SigwriterInstructions,
  SigwriterRxParameters,
  SystemGuid,
  isVisitInfo,
} from 'webcommon/shared';
import {
  DosingRepository,
} from 'webcommon/webapi';

const teaspoonsToMlMultiplier = 4.92892;
const tablespoonsToMlMultiplier = 14.7868;
const ozToMlMultiplier = 29.57353;

@Injectable({
  providedIn: 'root',
})
export class DosingService {
  static readonly ajsFactoryName = 'DosingServiceTs';

  constructor(
    private readonly aprimaCache: AprimaCacheService,
    private readonly doseRangeUtil: DoseRangeUtil,
    private readonly dosingRepository: DosingRepository,
    private readonly numUtil: NumUtil,
    private readonly serverInfoRepository: ServerInfoRepository,
    private readonly systemGuid: SystemGuid,
  ) {}

  private areCompatibleUnits(dosageUnits: string | null | undefined, drugDoseFormId: string) {
    const dosageUnitsBase = this.getBaseUnitFromSelection(dosageUnits);
    const drugDoseFormIdBase = this.getBaseUnitForDrug(drugDoseFormId);
    return dosageUnitsBase === drugDoseFormIdBase;
  }

  private getBaseUnitFromSelection(unitId: string | null | undefined): string | null | undefined {
    let baseline = unitId;
    switch (unitId) {
      case this.systemGuid.medicationUnitTablets:
      case this.systemGuid.medicationUnitCapsules:
        baseline = this.systemGuid.medicationUnitTablets;
        break;
      case this.systemGuid.medicationUnitPuffs:
      case this.systemGuid.medicationUnitDrops:
      case this.systemGuid.medicationUnitTeaspoons:
      case this.systemGuid.medicationUnitTablespoons:
      case this.systemGuid.medicationUnitOz:
      case this.systemGuid.medicationUnitMilliliters:
      case this.systemGuid.medicationUnitSpray:
        baseline = this.systemGuid.medicationUnitMilliliters;
        break;
      case this.systemGuid.medicationUnitGrams:
      case this.systemGuid.medicationUnitMilligrams:
      case this.systemGuid.medicationUnitMicrograms:
        baseline = this.systemGuid.medicationUnitMilligrams;
        break;
    }
    return baseline;
  }

  private getBaseUnitForDrug(unitId: string): string {
    // units that do not need conversion
    let baseline = unitId;
    switch (unitId) {
      case this.systemGuid.medicationUnitTablets:
      case this.systemGuid.medicationUnitCapsules:
        baseline = this.systemGuid.medicationUnitTablets;
        break;
      case this.systemGuid.medicationUnitPuffs:
      case this.systemGuid.medicationUnitDrops:
      case this.systemGuid.medicationUnitMilliliters:
      case this.systemGuid.medicationUnitSpray:
        baseline = this.systemGuid.medicationUnitMilliliters;
        break;
      case this.systemGuid.medicationUnitMilligrams:
        baseline = this.systemGuid.medicationUnitMilligrams;
        break;
    }
    return baseline;
  }

  convertToCommonUnit(dosageUnits: GuidStrValue | null | undefined, dosageAmount: number): number {
    switch (dosageUnits) {
      case this.systemGuid.medicationUnitTablets:
      case this.systemGuid.medicationUnitCapsules:
        break;
      // converts these to ml
      case this.systemGuid.medicationUnitTeaspoons:
        dosageAmount = this.numUtil.multiply(dosageAmount, teaspoonsToMlMultiplier);
        break;
      case this.systemGuid.medicationUnitTablespoons:
        dosageAmount = this.numUtil.multiply(dosageAmount, tablespoonsToMlMultiplier);
        break;
      case this.systemGuid.medicationUnitOz:
        dosageAmount = this.numUtil.multiply(dosageAmount, ozToMlMultiplier);
        break;
      case this.systemGuid.medicationUnitPuffs:
      case this.systemGuid.medicationUnitDrops:
      case this.systemGuid.medicationUnitMilliliters:
        break;
      // converts these to mg
      case this.systemGuid.medicationUnitGrams:
        dosageAmount = this.numUtil.multiply(dosageAmount, 1000);
        break;
      case this.systemGuid.medicationUnitMicrograms:
        dosageAmount = this.numUtil.divide(dosageAmount, 1000);
        break;
      case this.systemGuid.medicationUnitMilligrams:
        break;
      default:
        break;
    }
    return dosageAmount;
  }

  getDoseRangeInfo(
    ageInDays: number | null | undefined,
    medId: number | null | undefined,
    gcnSeqNo: number | null | undefined,
    weightKg: number | null | undefined,
    heightCm: number | null | undefined,
    gestationalAgeInWeeks: number | null | undefined,
    doseTypeIndicator: string | null | undefined,
    icdCode: string | null | undefined,
  ): Observable<DoseRangeInfo> {
    return this.dosingRepository.getDoseRangeInfo(
      ageInDays,
      medId,
      gcnSeqNo,
      weightKg,
      heightCm,
      gestationalAgeInWeeks,
      doseTypeIndicator,
      icdCode,
      this.aprimaCache.get('Default'),
    ).pipe(
      map(data => {
        const doseRanges = data.filter((item) => !!item);

        if (doseRanges.length === 0) {
          throw new Error('First Databank does not contain max/min dosing information for the selected medication.');
        }

        const result = doseRanges.map((item) => {
          return this.doseRangeUtil.getReadonlyDoseRangeInfo(item);
        });

        // TODO: handle case where there can be multiple results in this list that comes back
        // Right now, the backend doesn't support returning more than 1 result, but it could in the future
        return result[0];
      }),
    );
  }

  getDosingDetailsFull(visitId?: string | null, patientId?: string | null, defaultValue?: PatientDosingDetailsDto | null) {
    const hasVisitId = visitId && visitId !== Guid.empty();
    const hasPatientId = patientId && patientId !== Guid.empty();
    if (!hasVisitId && !hasPatientId) {
      return of(defaultValue);
    }

    const hasComprehensiveFeature = this.serverInfoRepository.isFeatureSupported('PatientDosingDetails_Comprehensive');
    if (hasComprehensiveFeature) {
      return this.dosingRepository.getComprehensiveDosingDetails(visitId, patientId);
    }

    if (hasVisitId) {
      return this.dosingRepository.getVisitDosingDetails(visitId);
    }

    if (hasPatientId) {
      return this.dosingRepository.getPatientDosingDetails(patientId);
    }

    return of(defaultValue);
  }

  getMinMaxWarning(
    sigInstructions: SigwriterInstructions,
    rxParameters: SigwriterRxParameters,
    patientDetails?: PatientDosingDetailsDto,
  ): Observable<string> {
    const dosageUnits = sigInstructions?.unit?.Id;
    const drugDoseFormId = rxParameters?.drug?.UnitId;
    if (!drugDoseFormId) {
      return of('Selected drug does not have dosing units. Unable to calculate recommended daily dosing.');
    }

    if (!this.areCompatibleUnits(dosageUnits, drugDoseFormId)) {
      return of('Dosing units are not compatible with drug. Unable to calculate recommended daily dosing.');
    }

    const params = this.mapDoseParams(sigInstructions, rxParameters, patientDetails);

    // convert to mg, ml, or leave as tablet/capsule
    const originalDosage = sigInstructions?.dosage?.Name;
    const dosageAmount = !originalDosage ? undefined : this.convertToCommonUnit(dosageUnits, parseFloat(originalDosage));

    return this.getDoseRangeInfo(
      params.ageInDays,
      params.medId,
      params.gcnSeqNo,
      params.weight,
      params.height,
      params.gestationalAge,
      params.doseTypeIndicator,
      params.icdCode,
    ).pipe(
      map(singleDosingInfo => {
        const minMaxWarningText = this.doseRangeUtil.GetMaxMinWarningMsg(
          singleDosingInfo,
          dosageAmount,
          params.frequencyPerDay,
          params.durationOfTherapyInDays,
        );

        return minMaxWarningText;
      }),
    );
  }

  getVariableDoseRanges(gcnSeqNo: number) {
    return this.dosingRepository.getVariableDoseRanges(
      gcnSeqNo,
      this.aprimaCache.get('Default'),
    );
  }

  mapDoseParams(
    sigInstructions: SigwriterInstructions,
    rxParameters: SigwriterRxParameters,
    patientDetails?: PatientDosingDetailsDto,
  ): DosingCalculatorModalParams {
    const params: DosingCalculatorModalParams = new DosingCalculatorModalParams();

    params.visitId = null;
    params.patientId = null;
    params.frequencyPerDay = sigInstructions?.frequency?.DosesPerDay;

    if (rxParameters) {
      if (rxParameters.drug) {
        params.medId = rxParameters.drug.MedId;
        params.gcnSeqNo = rxParameters.drug.GcnSeqNo;
      }

      if (rxParameters.visit) {
        if (isVisitInfo(rxParameters.visit)) {
          params.visitId = rxParameters.visit.visitId;
        } else {
          params.visitId = rxParameters.visit.VisitId;
        }
      }

      params.patientId = rxParameters.patientId;
    }

    // this is a newer concept, and we have no current way to derive it, so just leave it as null
    params.gestationalAge = null;

    // this is a newer concept, and we have no current way to derive it, so just leave it as null
    params.doseTypeIndicator = null;

    // TODO: fill in icdCode with something from sigwriter
    // but it has to be a single value
    params.icdCode = null;

    params.weight = null;
    params.weightDate = null;
    params.height = null;
    params.heightDate = null;
    params.ageInDays = null;
    if (patientDetails) {
      params.weight = patientDetails.WeightInKg;
      params.weightDate = patientDetails.WeightPerformedDate;
      params.height = patientDetails.HeightInCm;
      params.heightDate = patientDetails.HeightPerformedDate;
      params.ageInDays = patientDetails.AgeInDays;
    }

    params.durationOfTherapyInDays = null;

    // calculate difference in days between start and end dates
    if (sigInstructions && sigInstructions.startDate && sigInstructions.endDate) {
      params.durationOfTherapyInDays = moment(sigInstructions.endDate).diff(moment(sigInstructions.startDate), 'days');
    }

    return params;
  }
}
